<template>
    <div class="page-signup">
        <div class="block-title">
            Регистрация
            <Loader v-show="pending" class="loader" />
        </div>

        <div v-if="message" class="notification">
            <IconError class="icon" />
            <div class="text error" v-html="message" />
        </div>


        <div class="form-field">
            <div class="title">Фамилия, Имя, Отчество</div>
            <div class="value">
                <input v-model.trim="v.fio.$model"
                       :class="{ error: v.fio.$error }"
                       :disabled="disabledField"
                       class="fio"
                       autofocus
                       data-test-id="input-fio"
                       placeholder="Введите Фамилию, Имя, Отчество"
                       type="text" />
                <ErrorMessageTooltip :value="v.fio" bind-to=".fio" />
            </div>
        </div>

        <div class="form-field">
            <div class="title">Email пользователя</div>
            <div class="value">
                <input v-model="v.email.$model"
                       :class="{ error: v.email.$error }"
                       class="email"
                       placeholder="Введите адрес электронной почты"
                       type="text" />
                <ErrorMessageTooltip :value="v.email" bind-to=".email" />

                <TooltipOld :opened="isLdapEmail" bind-to=".email" class="tooltip inform small ldap" closeable>
                    <div class="container">
                        <IconInform class="icon" />
                        <div class="text">
                            Вы указали почту в домене rt.ru. <br>
                            Регистрация не требуется. Вам необходимо
                            <router-link class="accent" to="/user/login">вернуться на страницу авторизации
                            </router-link>
                            и продолжить вход по данным корпоративной учётной записи
                        </div>
                    </div>
                </TooltipOld>
            </div>
        </div>

        <div class="form-field">
            <div class="title">Придумайте пароль</div>
            <div class="value">
                <password v-model="v.password.$model"
                          :a-class="{ error: v.password.$error }"
                          :disabled="disabledField"
                          data-test-id="input-password"
                          placeholder="Введите пароль"/>
                <TooltipOld :opened="!!passwordError" bind-to="[data-test-id=input-password]"
                         class="error" trigger-on="focus">{{ passwordError }}
                </TooltipOld>
                <TooltipOld :opened="!passwordError && !safetyHard" bind-to="[data-test-id=input-password]"
                         class="inform password" trigger-on="focus">
                    <SafetyPassword :password="password" />
                </TooltipOld>
            </div>
        </div>

        <div class="form-field">
            <div class="title">Повторите пароль</div>
            <div class="value">
                <password v-model="v.password2.$model"
                          :a-class="{ error: v.password2.$error }"
                          :disabled="disabledField"
                          data-test-id="input-password2"
                          placeholder="Введите пароль повторно" />
                <TooltipOld :opened="!!passwordConfirmError" bind-to="[data-test-id=input-password2]"
                         class="error" trigger-on="focus">{{ passwordConfirmError }}
                </TooltipOld>
            </div>
        </div>

        <div class="form-field">
            <div class="value">
                <checkbox v-model="v.processingAgreed.$model"
                          :disabled="disabledField" class="processing-agreed">
                    Настоящим подтверждаю, что заполненные данные принадлежат мне,
                    являются достоверными и что я надлежащим образом и в полном объеме ознакомлен с
                    <a class="accent" download="УСЛОВИЯ ОБРАБОТКИ ПЕРСОНАЛЬНЫХ ДАННЫХ.pdf" href="/public/TPDP.pdf"
                       target="_blank">"Условиями обработки
                        персональных
                        данных"</a>
                    и даю свое согласие на обработку персональных данных
                    на указанных условиях
                </checkbox>
            </div>
        </div>

        <div class="form-field form-action">
            <div class="value">
                <button :disabled="v.$invalid || pending || disabledField"
                        class="primary fullwidth action"
                        data-test-id="button-signup"
                        @click="signup">Зарегистрироваться
                </button>
            </div>
        </div>

        <div class="form-field form-action login">
            <div class="value">
                <router-link class="fullwidth action" tag="button" to="/user/login">Есть аккаунт? Перейти к входу
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import { isLdapEmail } from '@/common/allowed.emails.js';
import { allowedEmailRule, duplicatedEmailRule, emailRulesDefault } from '@/common/util/validations/email.js';
import { fioRulesDefault } from '@/common/util/validations/fio.js';
import { requiredRule } from '@/common/util/validations/main.js';
import { entropy } from '@/common/validators.js';
import { signup } from '@/restapi/user/user.js';
import { useVuelidate } from '@vuelidate/core';
import { minLength, required, sameAs } from '@vuelidate/validators';
import SafetyPwdMixin from '@/common/comp/safety_password/safety.pwd.mixin.js';
import SafetyPassword from '@/common/comp/safety_password/SafetyPassword.vue';

import IconInform from '../icons/inform.svg?component';
import IconError from '@/pages/user/icons/error.svg?component';

export default {
    mixins: [SafetyPwdMixin],
    components: {
        IconError,
        SafetyPassword,
        IconInform,
    },
    setup() {
        return {
            v: useVuelidate(),
        };
    },
    data() {
        return {
            fio: '',
            email: '',
            password: '',
            password2: '',
            message: '',
            processingAgreed: false,

            pending: false,
        };
    },
    computed: {
        isLdapEmail() {
            return isLdapEmail(this.email);
        },
        fioError() {
            if (!this.v.fio.$dirty)
                return null;

            if (this.v.fio.required.$invalid)
                return 'Не указано ФИО';

            if (this.v.fio.fio.$invalid)
                return 'ФИО указано неверно';

            return null;
        },
        passwordError() {
            if (!this.v.password.$dirty)
                return null;

            if (this.v.password.required.$invalid)
                return 'Не указан пароль';

            return null;
        },
        passwordConfirmError() {
            if (!this.v.password2.$dirty)
                return null;

            if (this.v.password2.required.$invalid)
                return 'Подтвердите пароль';

            if (this.v.password2.sameAs.$invalid)
                return 'Пароли не совпадают';

            return null;
        },
        disabledField() {
            return this.isLdapEmail;
        },
    },
    validations() {
        return {
            fio: {
                ...fioRulesDefault(),
            },
            email: {
                ...requiredRule(),
                ...emailRulesDefault(),
                ...allowedEmailRule(),
                ...duplicatedEmailRule(),
            },
            password: {
                required,
                minLength: minLength(8),
                entropy: entropy(24),
            },
            password2: {
                required,
                sameAs: sameAs(this.password),
            },
            processingAgreed: { checked: () => (this.processingAgreed) },
        };
    },
    methods: {
        async signup() {
            this.v.$touch();

            if (this.v.$invalid)
                return;

            try {
                this.pending = true;

                await signup({
                    fio: this.fio,
                    email: this.email,
                    password: this.password,
                    processingAgreed: this.processingAgreed,
                });

                this.$router.push('/user/signup/await');

            } catch (e) {
                if (e.response.data.errorCode === 'error.MailSendException') {
                    this.message = 'Не удалось отправить письмо для подтверждения регистрации. Для завершения регистрации попробуйте через некоторое время воспользоваться восстановлением пароля. Если ошибка не исчезнет, обратитесь к администратору витрины.';
                    return;
                }

                this.message = e.message;
            } finally {
                this.pending = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../common/comp/safety_password/safety.pwd';

.page-signup {
    .processing-agreed {
        font-size: 12px;
        line-height: 16px;

        :deep {
            .icon {
                align-self: normal;
                margin-top: 4px;
            }
        }

        a {
            display: contents;
        }
    }

    .login {
        margin-top: 12px;
        display: block;
    }
}

.tooltip {
    .desc-fio {
        margin: 0;
        padding: 5px 20px;

        li {
            font-weight: 400;
            font-size: 12px;
        }
    }
}

.tooltip.ldap .container {
    display: flex;

    .icon {
        flex: none;
        margin-right: 8px;
    }
}
</style>
